import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { UserConnectionItem } from '@wlb/shared/types/userProfile';
import type { ConnectionAction } from '@wlb/shared/hooks/useUserConnections';
import { Avatar } from '../../../components/ui/Avatar';
import { PostMenuIcon } from '../../../components/ui/icons';
import {
  connectionAvatarUrl,
  connectionDisplayName,
  connectionProfileGuid,
} from './connectionUser';
import './connections.css';

interface ConnectionRowProps {
  user: UserConnectionItem;
  onOpenProfile: (user: UserConnectionItem) => void;
  /** Owner only: the overflow menu is hidden on another member's list. */
  onOpenMenu?: (user: UserConnectionItem, anchorRect: DOMRect) => void;
  menuOpen?: boolean;
  /** When provided, inline Accept/Reject buttons replace the kebab menu. */
  onAccept?: (user: UserConnectionItem) => void;
  onReject?: (user: UserConnectionItem) => void;
  actionLoading?: { userId: number; action: ConnectionAction } | null;
}

export function ConnectionRow({
  user,
  onOpenProfile,
  onOpenMenu,
  menuOpen = false,
  onAccept,
  onReject,
  actionLoading = null,
}: ConnectionRowProps) {
  const { t } = useTranslation();
  const name = connectionDisplayName(user);
  const showInlineActions = Boolean(onAccept && onReject);
  // A row without a profile GUID (deleted or unavailable member) stays plain
  // text rather than offering a link that dead-ends.
  const canOpenProfile = Boolean(connectionProfileGuid(user));
  const avatar = (
    <Avatar
      src={connectionAvatarUrl(user)}
      name={name}
      username={user.userName}
      seed={connectionProfileGuid(user) ?? user.userId}
      size="lg"
    />
  );

  const isAcceptLoading = actionLoading?.userId === user.userId && actionLoading?.action === 'acceptRequest';
  const isRejectLoading = actionLoading?.userId === user.userId && actionLoading?.action === 'rejectRequest';
  const isAnyLoading = actionLoading !== null;

  return (
    <li className="connection-row">
      {/* Avatar and name are separate controls: the row itself is not clickable,
          so it stays free for the overflow menu and future row actions. */}
      <div className="connection-row__identity">
        {canOpenProfile ? (
          <>
            <button
              type="button"
              className="connection-row__avatar-action"
              aria-label={t('home.accessibility.openUserProfile', { username: name })}
              onClick={() => onOpenProfile(user)}
            >
              {avatar}
            </button>
            <button
              type="button"
              className="connection-row__name connection-row__name-action"
              onClick={() => onOpenProfile(user)}
            >
              {name}
            </button>
          </>
        ) : (
          <>
            {avatar}
            <span className="connection-row__name">{name}</span>
          </>
        )}
      </div>

      {showInlineActions ? (
        <div className="connection-row__actions">
          <button
            type="button"
            className="connection-row__btn connection-row__btn--accept"
            disabled={isAnyLoading}
            onClick={event => { event.stopPropagation(); onAccept!(user); }}
          >
            {isAcceptLoading ? t('common.loading', 'Loading...') : t('home.notifications.buttons.accept')}
          </button>
          <button
            type="button"
            className="connection-row__btn connection-row__btn--reject"
            disabled={isAnyLoading}
            onClick={event => { event.stopPropagation(); onReject!(user); }}
          >
            {isRejectLoading ? t('common.loading', 'Loading...') : t('home.notifications.buttons.reject')}
          </button>
        </div>
      ) : onOpenMenu && (
        <button
          type="button"
          className="connection-row__menu"
          aria-label={t('connections.accessibility.openUserMenu', { name })}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={event => onOpenMenu(user, event.currentTarget.getBoundingClientRect())}
        >
          <PostMenuIcon size={20} />
        </button>
      )}
    </li>
  );
}
